import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { isTimeoutError } from '@/api/helpers.ts';
import { useInsuranceSearchMutation } from '@/api/hooks/mutations/use-insurance-search-mutation';
import animationData from '@/assets/lotties/eligibility-loader.json';
import { Heading1 } from '@/components/common/Heading1.tsx';
import { Lottie } from '@/components/common/lottie-web/Lottie.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { TextRotator } from '@/components/common/TextRotator.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import { reset, setVerifiedInsurances } from '@/types/insurance-builder.type.ts';
import { PLAN_FAMILY } from '@/types/insurance.type.ts';

const DISCOVERY_MESSAGES = [
  'Checking for Medicare & Medicaid coverage...',
  "We're looking up your benefits...",
  'Reviewing your information...',
  'Confirming what supplies are covered...',
  'Almost finished...',
  'Thank you for your patience. We are still confirming your coverage...',
];

export function InsuranceDiscoveryPending() {
  const navigate = useNavigate();
  const insuranceSearchMutation = useInsuranceSearchMutation();
  const {
    insuranceZipCode,
    insuranceBuilder: builder,
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
    patientRecipient,
  } = useSessionStore();

  const discoverInsurance = () => {
    const hasValidPatientData =
      patientFirstName &&
      patientLastName &&
      patientDateOfBirth &&
      patientGender &&
      patientRecipient;

    if (!hasValidPatientData) {
      logger.error(
        new Error(
          'Insurance Discovery Pending - patient data is not valid for discovering insurance'
        )
      );

      toast.dismiss();
      toast.error('We need to double check your personal information.', {
        action: {
          label: 'Continue',
          onClick: () => {
            navigate({ to: '/1', replace: true });
          },
        },
        duration: Infinity,
      });
      return;
    }

    const patientData = {
      recipient: patientRecipient as 'me' | 'someone-else',
      firstName: patientFirstName,
      lastName: patientLastName,
      dateOfBirth: patientDateOfBirth,
      gender: patientGender as 'male' | 'female',
    };

    const resetBuilder = reset(builder);
    useSessionStore.getState().updateState({ insuranceBuilder: resetBuilder });

    insuranceSearchMutation.mutate(
      { ...patientData, zipCode: insuranceZipCode },
      {
        onSuccess: (discoveredInsurances) => {
          const updatedBuilder = setVerifiedInsurances(resetBuilder, ...discoveredInsurances);

          useSessionStore.getState().updateState({
            insuranceBuilder: {
              ...updatedBuilder,
              discoveredInsurances,
              ranDiscovery: true,
            },
          });

          // Route to resolve page starting with Medicare
          navigate({
            to: '/insurance/resolve',
            search: { planFamily: PLAN_FAMILY.MEDICARE },
            replace: true,
          });
        },
        onError: (error) => {
          if (isTimeoutError(error)) {
            logger.error('Insurance search timed out, treating as no results found', error);
          } else {
            logger.error('Failed to search for insurance', error);
          }

          // Even on error, proceed to resolve page with empty results
          useSessionStore
            .getState()
            .updateState({ insuranceBuilder: { ...resetBuilder, ranDiscovery: true } });
          navigate({
            to: '/insurance/resolve',
            search: { planFamily: PLAN_FAMILY.MEDICARE },
            replace: true,
          });
        },
      }
    );
  };

  useEffect(
    function discoverInsuranceOnMount() {
      discoverInsurance();
    },
    // oxlint-disable-next-line exhaustive-deps
    []
  );

  const retry = () => {
    insuranceSearchMutation.reset();
    discoverInsurance();
  };

  const toPatientInfo = () => navigate({ to: '/1', replace: true });

  return (
    <section>
      <NavigationBar onBack={toPatientInfo} backLabel="Patient info" stepIndicator="2 of 3" />
      <StepperCard currentStep={2} totalSteps={3}>
        {insuranceSearchMutation.isPending && (
          <div
            className="flex flex-col items-center justify-center space-y-8 sm:space-y-12"
            aria-label="Loading..."
            aria-describedby="insurance-check-loader"
            aria-busy="true"
            role="status"
          >
            <Heading1 className="pb-0 text-center">Looking up your benefits...</Heading1>

            <Lottie animationData={animationData} className="aspect-square size-[80px]" />

            <div className="flex flex-col items-center gap-4">
              <TextRotator messages={DISCOVERY_MESSAGES} />
              <p className="text-center text-sm text-charcoal-500">This may take a minute.</p>
            </div>
          </div>
        )}

        {insuranceSearchMutation.isError && (
          <div
            className="flex flex-col items-center justify-center space-y-6"
            role="alert"
            aria-live="assertive"
            aria-describedby="insurance-check-error"
          >
            <Heading1 className="pb-0 text-center">Unable to look up your benefits</Heading1>

            <div className="flex flex-col items-center space-y-4">
              <p id="insurance-check-error" className="text-center text-base text-charcoal-500">
                An error occurred while looking up your benefits. Please try again.
              </p>

              <Button onClick={retry} className="mt-4">
                Try Again
              </Button>
            </div>
          </div>
        )}
      </StepperCard>
    </section>
  );
}
